import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  SaveAlt as SaveAltIcon,
  Search as SearchIcon,
  TrackChanges as TrackChangesIcon,
} from '../icons';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Chip,
  Card,
  CardContent,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { usePublicTheme } from '../../contexts/PublicThemeContext';
import { useCurrencyFormatter } from '../../contexts/CurrencyContext';
import orderService from '../../services/orderService';
import websocketService from '../../services/websocketService';
import { printReceipt, saveReceipt } from '../../utils/receiptUtils';
import { getHoverColor, hexToRgba } from '../../utils/colorUtils';
import HorizontalProgressBar from './tracking/HorizontalProgressBar';
import { getErrorMessage } from '../../utils/errorUtils';
import { getPaymentStatusConfig } from '../../config';
import { formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';

// Styled components with MenuPage theming
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: { xs: 16, sm: 28 },
    padding: 0,
    maxWidth: 680,
    width: '100%',
    maxHeight: { xs: '95vh', sm: '90vh' },
    boxShadow: '0 30px 60px rgba(0, 0, 0, 0.3)',
    overflow: 'hidden',
    margin: { xs: 1, sm: 2 },
  },
}));

const DialogHeader = styled(Box)(({ theme, primarycolor }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(3, 3.5),
  background: primarycolor
    ? `linear-gradient(135deg, ${primarycolor} 0%, ${getHoverColor(primarycolor, 'darken', 15)} 100%)`
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 2.5),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

const ScrollableContent = styled(Box)({
  maxHeight: 'calc(90vh - 180px)',
  overflowY: 'auto',
  overflowX: 'hidden',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
});

const OrderInfoCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  marginBottom: theme.spacing(2),
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
  border: '1px solid #f0f0f0',
}));

const ItemContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  padding: theme.spacing(2, 0),
  '&:not(:last-child)': {
    borderBottom: '1px solid #f5f5f5',
  },
}));

const ItemQuantity = styled(Box)(({ primarycolor }) => ({
  backgroundColor: primarycolor || '#667eea',
  color: '#fff',
  borderRadius: '50%',
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.875rem',
  fontWeight: 700,
  marginRight: 12,
  flexShrink: 0,
  boxShadow: `0 2px 8px ${hexToRgba(primarycolor || '#667eea', 0.3)}`,
}));


const TrackOrderDialog = ({ open, onClose, initialOrderNumber = '' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { primaryColor: themePrimaryColor, updateMerchantTheme } = usePublicTheme();
  const { formatCurrency } = useCurrencyFormatter();

  // State for input view
  const [orderNumber, setOrderNumber] = useState(initialOrderNumber);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // State for tracking view
  const [viewMode, setViewMode] = useState(initialOrderNumber ? 'tracking' : 'input'); // 'input' or 'tracking'
  const [order, setOrder] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);

  // Update merchant theme when order data is available
  useEffect(() => {
    if (order?.merchant?.settings) {
      updateMerchantTheme(order.merchant.settings);
    }
  }, [order?.merchant?.settings, updateMerchantTheme]);

  // Use primary color from theme context (should be updated when order data is loaded)
  const primaryColor = themePrimaryColor;

  // Handle real-time order updates from WebSocket
  const handleOrderUpdate = useCallback((data) => {
    console.log('🔄 TrackOrderDialog: Received WebSocket order update:', data);

    if (data.order && order && (
      data.order.order_number === order.order_number ||
      data.order.id === order.id
    )) {
      console.log('✅ TrackOrderDialog: Update matches current order, updating state');
      setOrder(prevOrder => ({
        ...prevOrder,
        ...data.order
      }));
    }
  }, [order]);

  // Handle WebSocket connection status changes
  const handleConnectionChange = useCallback((connected) => {
    console.log(`🔌 TrackOrderDialog: WebSocket ${connected ? 'connected' : 'disconnected'}`);
    setWsConnected(connected);
  }, []);

  // Set up WebSocket connection when order is loaded
  useEffect(() => {
    if (order && order.merchant && order.merchant.id && viewMode === 'tracking') {
      console.log('🔌 TrackOrderDialog: Setting up WebSocket connection for merchant:', order.merchant.id);

      websocketService.connect(order.merchant.id);

      const removeOrderUpdateListener = websocketService.onOrderUpdate(handleOrderUpdate);
      const removeConnectedListener = websocketService.onConnected(() => handleConnectionChange(true));
      const removeDisconnectedListener = websocketService.onDisconnected(() => handleConnectionChange(false));

      setWsConnected(websocketService.isConnected());

      return () => {
        console.log('🔌 TrackOrderDialog: Cleaning up WebSocket listeners');
        removeOrderUpdateListener();
        removeConnectedListener();
        removeDisconnectedListener();
      };
    }
  }, [order, viewMode, handleOrderUpdate, handleConnectionChange]);

  // Cleanup on close
  useEffect(() => {
    if (!open) {
      // Reset state when dialog is closed
      setViewMode(initialOrderNumber ? 'tracking' : 'input');
      setOrderNumber(initialOrderNumber);
      setError('');
      setOrder(null);
      setWsConnected(false);
    }
  }, [open, initialOrderNumber]);

  // Auto-fetch order when initialOrderNumber is provided
  useEffect(() => {
    if (open && initialOrderNumber && viewMode === 'tracking' && !order) {
      handleTrackOrder(initialOrderNumber);
    }
  }, [open, initialOrderNumber, viewMode, order]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    if (viewMode === 'tracking') {
      // Disconnect WebSocket before closing
      websocketService.disconnect();
    }
    onClose();
  };

  const handleBackToInput = () => {
    setViewMode('input');
    setOrder(null);
    setOrderNumber('');
    setError('');
    websocketService.disconnect();
  };

  const handleTrackOrder = async (targetOrderNumber = null) => {
    const orderNumToUse = targetOrderNumber || orderNumber.trim();

    if (!orderNumToUse) {
      setError('Please enter your order number');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const orderData = await orderService.getPublicOrderByNumber(orderNumToUse);

      if (orderData && orderData.order_number) {
        setOrder(orderData);
        setViewMode('tracking');
      } else {
        setError('Order not found. Please check your order number and try again.');
      }
    } catch (error) {
      console.error('Error tracking order:', error);
      if (error.response && error.response.status === 404) {
        setError('Order not found. Please check your order number and try again.');
      } else {
        const errorMessage = error.userMessage || getErrorMessage(error, 'Unable to track order. Please try again later.');
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!order) return;

    try {
      setRefreshing(true);
      const orderData = await orderService.getPublicOrderByNumber(order.order_number);
      setOrder(orderData);
    } catch (error) {
      console.error('Error refreshing order:', error);
      const errorMessage = error.userMessage || getErrorMessage(error, 'Failed to refresh order data');
      setError(errorMessage);
    } finally {
      setRefreshing(false);
    }
  };

  const handlePrintReceipt = () => {
    if (order) {
      printReceipt(order, order.merchant, formatCurrency);
    }
  };

  const handleSaveReceipt = () => {
    if (order) {
      saveReceipt(order, order.merchant, formatCurrency);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && viewMode === 'input') {
      handleTrackOrder();
    }
  };

  const handleCopyOrderNumber = async () => {
    if (order?.order_number) {
      try {
        await navigator.clipboard.writeText(order.order_number);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
      } catch (error) {
        console.error('Failed to copy order number:', error);
      }
    }
  };

  const formatDateTime = (dateString) => formatDateTimeUtil(dateString);

  // Render input view
  const renderInputView = () => (
    <>
      <DialogHeader primarycolor={primaryColor}>
        <Typography
          variant="h5"
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 800,
            letterSpacing: '-0.01em',
            color: '#fff',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            flex: 1,
            minWidth: 0,
          }}
        >
          Track Your Order
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: '#fff',
            borderRadius: 2,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            flexShrink: 0,
            '&:hover': {
              transform: 'rotate(90deg) scale(1.1)',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogHeader>

      <DialogContent sx={{ px: { xs: 2, sm: 4 }, py: { xs: 3, sm: 4 } }}>
        <Box textAlign="center" mb={3}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: 64, sm: 80 },
              height: { xs: 64, sm: 80 },
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${primaryColor || '#667eea'} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 15)} 100%)`,
              color: '#fff',
              margin: '0 auto 20px auto',
              boxShadow: `0 8px 25px ${hexToRgba(primaryColor || '#667eea', 0.4)}`,
            }}
          >
            <TrackChangesIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />
          </Box>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 500
            }}
          >
            Enter your order number to track your order status
          </Typography>
        </Box>

        <TextField
          autoFocus
          fullWidth
          label="Order Number"
          placeholder="e.g., TES123ABC"
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
          variant="outlined"
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              fontSize: { xs: '1rem', sm: '1.2rem' },
              fontFamily: 'monospace',
              fontWeight: 600,
              borderRadius: { xs: 3, sm: 4 },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
              },
              '&.Mui-focused': {
                boxShadow: `0 8px 25px ${hexToRgba(primaryColor || '#667eea', 0.25)}`,
              }
            }
          }}
          helperText="Order numbers are case-insensitive"
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{
          backgroundColor: '#f8f9fa',
          borderRadius: 3,
          p: 2.5,
          border: '1px solid #e9ecef'
        }}>
          <Typography variant="subtitle2" gutterBottom sx={{
            fontWeight: 700,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: '0.95rem'
          }}>
            Need Help?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
            • Order numbers are typically 6-9 characters long
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
            • Check your email confirmation or receipt
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            • Contact the restaurant if you can't find your order number
          </Typography>
        </Box>
      </DialogContent>

      <Box sx={{
        px: { xs: 2, sm: 4 },
        pb: { xs: 3, sm: 4 },
        display: 'flex',
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        gap: { xs: 1.5, sm: 2 },
        justifyContent: 'flex-end'
      }}>
        <Button
          onClick={handleClose}
          variant="text"
          fullWidth={isMobile}
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            borderRadius: { xs: 3, sm: 4 },
            px: { xs: 3, sm: 4 },
            py: { xs: 1.25, sm: 1.5 },
            fontWeight: 600,
            fontSize: { xs: '0.9rem', sm: '1rem' },
            textTransform: 'none',
            color: '#6b7280',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              color: '#374151',
              transform: 'translateY(-2px)',
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => handleTrackOrder()}
          variant="contained"
          disabled={loading || !orderNumber.trim()}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
          fullWidth={isMobile}
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            borderRadius: { xs: 3, sm: 4 },
            px: { xs: 3, sm: 5 },
            py: { xs: 1.5, sm: 1.5 },
            fontWeight: 700,
            fontSize: { xs: '0.95rem', sm: '1rem' },
            textTransform: 'none',
            background: `linear-gradient(135deg, ${primaryColor || '#667eea'} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 15)} 100%)`,
            boxShadow: `0 8px 25px ${hexToRgba(primaryColor || '#667eea', 0.35)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: `linear-gradient(135deg, ${getHoverColor(primaryColor || '#667eea', 'darken', 10)} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 20)} 100%)`,
              transform: 'translateY(-3px)',
              boxShadow: `0 12px 35px ${hexToRgba(primaryColor || '#667eea', 0.45)}`,
            },
            '&:disabled': {
              background: '#ccc',
              color: '#666',
            }
          }}
        >
          {loading ? 'Tracking...' : 'Track Order'}
        </Button>
      </Box>
    </>
  );

  // Render tracking view
  const renderTrackingView = () => (
    <>
      <DialogHeader primarycolor={primaryColor}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={handleBackToInput}
            size="small"
            sx={{
              color: '#fff',
              borderRadius: 2,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 800,
                color: '#fff',
                fontSize: '1.25rem',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                Order #{order?.order_number}
              </Typography>
              <IconButton
                onClick={handleCopyOrderNumber}
                size="small"
                sx={{
                  color: copied ? '#4caf50' : 'rgba(255, 255, 255, 0.8)',
                  borderRadius: 2,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: copied ? '#4caf50' : '#fff',
                  }
                }}
                title="Copy Order Number"
              >
                {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Box>
            <Typography variant="caption" sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: '0.8rem'
            }}>
              {formatDateTime(order?.created_at)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {wsConnected && (
            <Chip
              label="Live"
              size="small"
              sx={{
                backgroundColor: 'rgba(76, 175, 80, 0.9)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 24,
              }}
            />
          )}
          <IconButton
            onClick={handleRefresh}
            disabled={refreshing}
            size="small"
            sx={{
              color: '#fff',
              borderRadius: 2,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }
            }}
          >
            <RefreshIcon />
          </IconButton>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: '#fff',
              borderRadius: 2,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'rotate(90deg) scale(1.1)',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogHeader>

      <ScrollableContent>
        <DialogContent sx={{ px: 3, py: 3 }}>
          {/* Order Progress - Only show when order is loaded */}
          {order && (
            <Box sx={{ mb: 2 }}>
              <HorizontalProgressBar order={order} />
            </Box>
          )}

          {/* Restaurant Info - Only show when order is loaded */}
          {order?.merchant && (
            <OrderInfoCard>
              <CardContent>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: primaryColor || '#333',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: '1.1rem',
                  mb: 2
                }}>
                  Restaurant Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                    {order.merchant.name}
                  </Typography>
                  {order.merchant.address && (
                    <Typography variant="body2" color="text.secondary">
                      {order.merchant.address}
                    </Typography>
                  )}
                  {order.merchant.phone && (
                    <Typography variant="body2" color="text.secondary">
                      Phone: {order.merchant.phone}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </OrderInfoCard>
          )}

          {/* Order Items - Only show when order is loaded */}
          {order && (
            <OrderInfoCard>
              <CardContent>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: primaryColor || '#333',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: '1.1rem',
                  mb: 2
                }}>
                  Order Items
                </Typography>

                {order?.items?.map((item, index) => (
                <ItemContainer key={item.id || index}>
                  <ItemQuantity primarycolor={primaryColor}>
                    {item.quantity}
                  </ItemQuantity>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}>
                      {item.menu_item?.name || item.name || 'Item'}
                    </Typography>
                    {item.options && item.options.length > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', ml: 1.5, mt: 0.25 }}>
                        Base {formatCurrency(Number(item.price) || 0)}
                        {item.options.map((opt) =>
                          ` · ${opt.name || opt.option_name}${Number(opt.price_addition) > 0 ? ` +${formatCurrency(Number(opt.price_addition))}` : ''}`
                        ).join('')}
                      </Typography>
                    )}
                    {item.notes && (
                      <Typography variant="body2" sx={{
                        mt: 0.5,
                        ml: 1.5,
                        fontStyle: 'italic',
                        color: '#856404',
                        fontSize: '0.85rem'
                      }}>
                        Note: {item.notes}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#333', ml: 2 }}>
                    {formatCurrency(Number(item.total))}
                  </Typography>
                </ItemContainer>
              ))}

              <Divider sx={{ my: 2 }} />

              {/* Totals */}
              <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 2, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatCurrency(order?.subtotal)}
                  </Typography>
                </Box>
                {order?.tax > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Tax</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(order?.tax)}
                    </Typography>
                  </Box>
                )}
                {order?.service_charge > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Service Charge</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(order?.service_charge)}
                    </Typography>
                  </Box>
                )}
                {order?.discount_details && order.discount_details.length > 0 ? (
                  order.discount_details.map((disc) => (
                    <Box key={disc.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">{disc.name}</Typography>
                        {disc.is_auto && (
                          <Chip
                            label="Auto"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: '#dbeafe',
                              color: '#1d4ed8',
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#16a34a' }}>
                        -{formatCurrency(Number(disc.amount))}
                      </Typography>
                    </Box>
                  ))
                ) : order?.discount > 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Discount</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#16a34a' }}>
                      -{formatCurrency(order?.discount)}
                    </Typography>
                  </Box>
                ) : null}
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>Total</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: primaryColor }}>
                    {formatCurrency(order?.total)}
                  </Typography>
                </Box>
              </Box>

              {/* Payment Status */}
              {order?.payment_status && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Payment Status:</Typography>
                  <Chip
                    icon={order.payment_status === 'COMPLETED' ? <CheckCircleIcon /> : null}
                    label={getPaymentStatusConfig(order.payment_status).label}
                    size="small"
                    sx={{
                      backgroundColor: getPaymentStatusConfig(order.payment_status).color,
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      '& .MuiChip-icon': { color: '#fff' }
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </OrderInfoCard>
          )}

          {/* Contact Information */}
          {order?.merchant && (
            <OrderInfoCard>
              <CardContent>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: primaryColor || '#333',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: '1.1rem',
                  mb: 2
                }}>
                  Contact Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                    {order.merchant.name}
                  </Typography>
                  {order.merchant.phone && (
                    <Typography variant="body2" color="text.secondary">
                      Phone: {order.merchant.phone}
                    </Typography>
                  )}
                  {order.merchant.address && (
                    <Typography variant="body2" color="text.secondary">
                      {order.merchant.address}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </OrderInfoCard>
          )}

          {/* Receipt Buttons - Only show for paid orders */}
          {order?.payment_status === 'COMPLETED' && (
            <Box sx={{
              display: 'flex',
              gap: 2,
              mt: 2,
              p: 2,
              backgroundColor: getHoverColor(primaryColor, 'lighten', 90),
              borderRadius: 3,
              border: `2px solid ${primaryColor}`,
            }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PrintIcon />}
                onClick={handlePrintReceipt}
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  borderRadius: 3,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  backgroundColor: primaryColor,
                  boxShadow: `0 4px 12px ${hexToRgba(primaryColor, 0.3)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: getHoverColor(primaryColor, 'darken', 15),
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 16px ${hexToRgba(primaryColor, 0.4)}`,
                  }
                }}
              >
                Print Receipt
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<SaveAltIcon />}
                onClick={handleSaveReceipt}
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  borderRadius: 3,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  borderColor: primaryColor,
                  color: primaryColor,
                  borderWidth: 2,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: primaryColor,
                    color: '#fff',
                    borderWidth: 2,
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                Save Receipt
              </Button>
            </Box>
          )}
        </DialogContent>
      </ScrollableContent>
    </>
  );

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      {viewMode === 'input' ? renderInputView() : renderTrackingView()}
    </StyledDialog>
  );
};

export default TrackOrderDialog;
